import { Button, ConfigProvider, DatePicker, Form, Input, InputNumber, Modal, Popover, Spin, message } from 'antd'
import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useRequest } from 'ahooks'
import type { Dayjs } from 'dayjs'
import clsx from 'clsx'
import { ThEmpty } from '@/components'
import { createZbMilestoneApi, getZbMilestonesApi } from '@/services/apis/milestoneZb'
import { ProjectDetailContext } from '../constant'
import { k_permission } from '../project'
import { buildZbMilestones, zbErrorMessage } from './data'
import Edit from './edit'
import styles from './index.module.scss'

interface CreateValues {
  name: string
  planDate: Dayjs
  percentage: number
  sortOrder: number
  milestoneCode?: string
}

// Remount when switching projects so neither open forms nor cached requests can
// accidentally apply the previous project's inputs to the next project.
const MilestoneZb: React.FC = () => {
  const { projectID } = useParams()
  return <MilestoneZbPage key={projectID} projectID={projectID || ''} />
}

const MilestoneZbPage: React.FC<{ projectID: string }> = ({ projectID }) => {
  const { permissions = [] } = useContext(ProjectDetailContext)
  const canEdit = permissions.includes(k_permission.milestoneEdit) || permissions.includes(k_permission.opAdmin)
  const { data = [], loading, error, refresh } = useRequest(getZbMilestonesApi, { defaultParams: [projectID] })
  const [addOpen, setAddOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form] = Form.useForm<CreateValues>()
  const renderData = useMemo(() => buildZbMilestones(data), [data])

  const create = async () => {
    if (creating || !canEdit) return
    let values: CreateValues
    try {
      values = await form.validateFields()
    } catch {
      // Match the existing milestone page's message-based validation feedback.
      const firstError = form.getFieldsError().find(field => field.errors.length)?.errors[0]
      if (firstError) message.warning(firstError)
      return
    }
    setCreating(true)
    try {
      await createZbMilestoneApi(projectID, { ...values, name: values.name.trim(), milestoneCode: values.milestoneCode?.trim() || undefined, planDate: values.planDate.format('YYYY-MM-DD') })
      message.success('创建成功')
      setAddOpen(false)
      form.resetFields()
      refresh()
    } catch (error) {
      message.error(zbErrorMessage(error, '创建失败'))
    } finally {
      setCreating(false)
    }
  }

  const closeEdit = () => {
    setEditOpen(false)
    // Deletes are immediate; cancelling subsequent edits must still refresh
    // their effect on the timeline without saving any pending form changes.
    refresh()
  }

  return <Spin spinning={loading}>
    {(canEdit || data.length > 0) && <div className="mb-4 flex items-center gap-2">
      {canEdit && <Button type="primary" onClick={() => setAddOpen(true)}>新增节点</Button>}
      {/* Like the standard milestone page, the entry is visible to viewers too;
          计划日期/实际完成日期/状态对所有人开放编辑，readOnly 仅限制删除。 */}
      {data.length > 0 && <ConfigProvider theme={{ token: { colorPrimary: '#047857' } }}>
        <Button type="primary" onClick={() => setEditOpen(true)}>设置节点时间与状态</Button>
      </ConfigProvider>}
    </div>}
    {error ? <div className="py-12 text-center">
      <div className="mb-3">数据加载失败</div>
      <Button onClick={refresh}>重试</Button>
    </div> : !loading && renderData.length === 0 ? <ThEmpty className="mt-4" description="暂无数据" /> : <div className="flex h-auto items-center overflow-x-auto overflow-y-hidden px-8 pt-16 pb-4">
      {renderData.map((item, index) => <div key={item.id} className={clsx(styles['timeline-item'], item.status === 1 ? styles['is-outdate'] : item.overdue ? styles['is-overdue'] : '')}>
        <span className={styles['timeline-item-label']}>{item.displayDate}</span>
        <div className={styles['timeline-item-sep']}>
          {index > 0 && <div className={styles['timeline-item-line-box']}><div className={styles['timeline-item-line']} /></div>}
          {item.overdue ? <Popover content={`计划完成日期: ${item.planDate}，延期 ${item.overdueDays} 天`}>
            <div className={styles['timeline-item-taild']} />
          </Popover> : <div className={styles['timeline-item-taild']} />}
        </div>
        <div className={styles['timeline-item-content']}><div className="two-line-clamp">{item.name}</div>{item.milestoneCode ? <div>{item.milestoneCode}</div> : null}<div>{item.cumulativePercent}</div></div>
      </div>)}
    </div>}
    <Modal title="新增里程碑节点" open={addOpen && canEdit} destroyOnHidden confirmLoading={creating}
      maskClosable={!creating} closable={!creating} cancelButtonProps={{ disabled: creating }}
      onCancel={() => setAddOpen(false)} onOk={() => void create()}>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="节点名称" rules={[
          { required: true, whitespace: true, message: '请填写节点名称' },
          { validator: (_, value: string | undefined) => !value || Array.from(value.trim()).length <= 200 ? Promise.resolve() : Promise.reject(new Error('节点名称不能超过 200 个字符')) },
        ]}><Input disabled={creating} /></Form.Item>
        <Form.Item name="planDate" label="计划日期" rules={[{ required: true, message: '请选择计划日期' }]}>
          <DatePicker disabled={creating} className="w-full" />
        </Form.Item>
        <Form.Item name="percentage" label="进度占比" rules={[
          { required: true, message: '请输入进度占比' },
          { type: 'number', min: 0, max: 100, message: '进度占比须为 0-100 之间的数值' },
        ]}><InputNumber disabled={creating} addonAfter="%" className="w-full" /></Form.Item>
        <Form.Item name="sortOrder" label="排序值" rules={[
          { required: true, message: '请输入排序值' },
          { type: 'integer', message: '排序值须为整数' },
        ]}><InputNumber disabled={creating} precision={0} className="w-full" /></Form.Item>
        <Form.Item name="milestoneCode" label="里程碑CODE" rules={[
          { validator: (_, value: string | undefined) => !value || Array.from(value.trim()).length <= 64 ? Promise.resolve() : Promise.reject(new Error('里程碑CODE不能超过 64 个字符')) },
        ]}><Input disabled={creating} placeholder="可选，如 21" /></Form.Item>
      </Form>
    </Modal>
    <Modal title="节点时间与状态设置" width="fit-content" style={{ minWidth: 750 }} destroyOnHidden
      open={editOpen} onCancel={closeEdit} footer={null}>
      <Edit values={data} projectID={projectID} readOnly={!canEdit} onUpdate={closeEdit} onCancel={closeEdit} />
    </Modal>
  </Spin>
}

export default MilestoneZb
